import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  deleteTask,
  hideTask,
  rerunTask,
  stopTask,
  unhideTask,
} from '@/lib/api/generated/api';
import { personalAccountRefusal, type MissingHost } from '@/features/identity';
import { workspaceKeys } from '@/features/workspaces';
import { TASK_MUTATION_KEYS } from './taskMutationKeys';
import { useOwnTaskIds } from '../ownTaskIds';
import { classifyDeleteFailure, TaskDeleteError } from '../errors';
import { taskKeys } from '../queryKeys';
export interface TaskLifecycleInput {
  workspaceId: string;
  taskId: string;
  /** Outlive the page: an action held until it goes away (spec 154 R3). */
  keepalive?: boolean;
}

/**
 * Stopping, rerunning, hiding and deleting a task (specs 078, 137).
 *
 * All three invalidate the workspace aggregate rather than patching it. That is the
 * opposite of `useCreateTask`, which seeds its response, and the difference is that none
 * of these three returns the task: stopping changes an execution's status, rerunning
 * creates one, and hiding removes the task from the projection entirely. There is nothing
 * authoritative to seed, so the honest move is to re-read.
 */
function useInvalidateWorkspace() {
  const queryClient = useQueryClient();
  return (workspaceId: string) =>
    queryClient.invalidateQueries({
      queryKey: workspaceKeys.detail(workspaceId),
    });
}

/**
 * Interrupt a running task, and wait for confirmation.
 *
 * This request is deliberately slow — the server does not answer until the run has
 * actually stopped — so the UI must show it working rather than assume it was instant.
 * A 504 means the stop could not be confirmed: the run may still be going, which is a
 * different message from "it failed to stop".
 */
export function useStopTask() {
  const invalidate = useInvalidateWorkspace();

  return useMutation({
    mutationKey: TASK_MUTATION_KEYS.stop,
    mutationFn: async ({ workspaceId, taskId }: TaskLifecycleInput) => {
      const response = await stopTask(workspaceId, taskId);
      if (response.status === 200) return;
      if (response.status === 409) {
        throw new Error(
          'This task is not running, so there is nothing to stop.',
        );
      }
      if (response.status === 504) {
        throw new Error(
          'The task was asked to stop but has not been confirmed stopped. It may still be running — check again shortly.',
        );
      }
      throw new Error('The task could not be stopped.');
    },
    onSuccess: (_result, { workspaceId }) => invalidate(workspaceId),
  });
}

/** A rerun refused because the workspace requires the person's own account. */
export class PersonalAccountRefused extends Error {
  readonly hosts: MissingHost[];

  constructor(message: string, hosts: MissingHost[]) {
    super(message);
    this.name = 'PersonalAccountRefused';
    this.hosts = hosts;
  }
}

export function isPersonalAccountRefused(
  error: unknown,
): error is PersonalAccountRefused {
  return error instanceof PersonalAccountRefused;
}

/** Run the task's most recent execution again, on the same step. */
export function useRerunTask() {
  const invalidate = useInvalidateWorkspace();

  return useMutation({
    mutationKey: TASK_MUTATION_KEYS.rerun,
    mutationFn: async ({
      workspaceId,
      taskId,
      keepalive,
    }: TaskLifecycleInput) => {
      const response = await rerunTask(
        workspaceId,
        taskId,
        keepalive ? { keepalive } : undefined,
      );
      // 202, not 200: the run is dispatched, not finished.
      if (response.status === 202) return;
      if (response.status === 409) {
        // The service refuses for more than one reason — a run already in
        // flight, or nothing to run again — and says which in its envelope.
        // Its words win over a guess made here.
        throw new Error(
          response.data.message ??
            'This task is already running. Stop it before running it again.',
        );
      }
      if (response.status === 422) {
        // The workspace requires the person's own forge account (spec 160): its
        // own envelope, read first.
        const needsAccount = personalAccountRefusal(response.data);
        if (needsAccount) {
          throw new PersonalAccountRefused(
            needsAccount.message,
            needsAccount.hosts,
          );
        }
        // The step this task would rerun on has lost its linked AI profile
        // since the last run (spec 122) — one of the paths with no control
        // to disable in advance, so this is the only chance to name the
        // step. The route's OpenAPI doc does not yet declare this body (it
        // only documents FastAPI's default validation-error shape), but the
        // backend's global error handler still emits the stable domain
        // envelope `{ error, message }` for this status at runtime — read
        // defensively rather than trusting the generated `HTTPValidationError`
        // type, which does not describe what the server actually sends here.
        const body: unknown = response.data;
        const message =
          typeof body === 'object' &&
          body !== null &&
          'message' in body &&
          typeof (body as { message?: unknown }).message === 'string'
            ? (body as { message: string }).message
            : undefined;
        throw new Error(
          message ??
            'That step has no AI profile configured. Link one in the workflow editor before running tasks there.',
        );
      }
      throw new Error('The task could not be run again.');
    },
    onSuccess: (_result, { workspaceId }) => invalidate(workspaceId),
  });
}

/**
 * Take a task off the board, or put it back.
 *
 * Nothing is deleted, and no spend figure moves — the task's runs and their consumption
 * are untouched. Refused while a run is in flight, so a task cannot go on spending a
 * workspace's budget with nothing on the board to explain it.
 */
export function useSetTaskHidden() {
  const invalidate = useInvalidateWorkspace();

  return useMutation({
    mutationKey: TASK_MUTATION_KEYS.hide,
    mutationFn: async ({
      workspaceId,
      taskId,
      hidden,
    }: TaskLifecycleInput & { hidden: boolean }) => {
      const response = hidden
        ? await hideTask(workspaceId, taskId)
        : await unhideTask(workspaceId, taskId);
      if (response.status === 200) {
        // Put back by this person: it reappears at once, even while the page
        // holds other arrivals, and without the arrival wash (spec 148).
        if (!hidden) useOwnTaskIds.getState().add(taskId);
        return;
      }
      if (response.status === 409) {
        throw new Error(
          'This task is running. Stop it before hiding it, so its spending stays visible while it continues.',
        );
      }
      throw new Error(
        hidden
          ? 'The task could not be hidden.'
          : 'The task could not be restored.',
      );
    },
    onSuccess: (_result, { workspaceId }) => invalidate(workspaceId),
  });
}

/**
 * Delete a task and its subtasks for good (spec 137 US2).
 *
 * The service does not answer until any running work has stopped, so this can be
 * slow. Success and "already gone" both mean the task is no longer there, so both
 * re-read the board and drop the task's own cached reads; a refusal leaves the
 * board as it was.
 */
export function useDeleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: TASK_MUTATION_KEYS.delete,
    mutationFn: async ({ workspaceId, taskId }: TaskLifecycleInput) => {
      const response = await deleteTask(workspaceId, taskId);
      if (response.status === 204) return;
      throw classifyDeleteFailure(response.status, response.data);
    },
    onSettled: (_result, error, { workspaceId, taskId }) => {
      if (
        error &&
        !(error instanceof TaskDeleteError && error.kind === 'not-found')
      ) {
        return;
      }
      // The prefix reaches both the plain and the include-hidden aggregate.
      void queryClient.invalidateQueries({
        queryKey: workspaceKeys.detail(workspaceId),
      });
      // A deleted subtask is logged on its run (spec 148).
      void queryClient.invalidateQueries(
        taskKeys.activityInWorkspace(workspaceId),
      );
      // Only reads nothing is showing. The drawer that offered the delete is
      // still mounted here, and removing its live reads makes it refetch a task
      // that is gone (a 404 apiece) in the moment before it closes; those
      // unmount with it and age out on their own.
      queryClient.removeQueries({
        queryKey: ['task', taskId],
        type: 'inactive',
      });
    },
  });
}
